import { UzButton, UzDateTimePicker } from '@utazzo/ui';
import { AlertTriangle, Plus, Ship, Trash2 } from 'lucide-react';
import React from 'react';

import { CityPicker } from '@/components/pickers';

/**
 * Cruise-as-stay Phase 2 (rework). A cruise stay is entered EXACTLY like a
 * hotel leg: an embark CITY + check-in/check-out dates. NO specific cruise
 * is picked here - the server auto-selects a cruise (and cabin) for that
 * city once the day board is generated, the same way a preferred hotel is
 * auto-picked for a hotel leg's city. The operator changes the auto-picked
 * cruise later, on the day board (mirrors "Change hotel").
 */
export interface CruiseStayRow {
  id: string;
  cityId: string | null;
  /** Embark date, "YYYY-MM-DD". Null until the agent picks it. */
  checkIn: string | null;
  /** Disembark date, "YYYY-MM-DD". Null until the agent picks it. */
  checkOut: string | null;
}

/** "YYYY-MM-DD" -> local Date (midnight), for UzDateTimePicker. */
const ymdToDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
/** Date -> "YYYY-MM-DD" (local). */
const dateToYmd = (d: Date | null): string | null =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : null;
/** Today at local midnight - the earliest selectable embark date. */
const startOfToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
/** "YYYY-MM-DD" + n days -> "YYYY-MM-DD" (UTC-consistent, no IST off-by-one). */
const addDays = (iso: string, n: number): string => {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const daysBetween = (a?: string | null, b?: string | null): number => {
  if (!a || !b) return 0;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  if (Number.isNaN(ms)) return 0;
  return Math.max(0, Math.round(ms / 86_400_000));
};

interface CruiseConfigCardProps {
  rows: CruiseStayRow[];
  disabled: boolean;
  /** Prefer this country in the CityPicker's suggestions (mirrors the hotel legs). */
  countryId: string | null;
  /** rowId -> full, ready-to-render conflict/gap message(s) for that row. */
  conflictLabels: Map<string, string[]>;
  /** Appends a blank row (city null, dates null) - never opens a picker. */
  onAdd: () => void;
  onRemove: (id: string) => void;
  onChange: (
    id: string,
    patch: Partial<Pick<CruiseStayRow, 'cityId' | 'checkIn' | 'checkOut'>>,
  ) => void;
}

export const CruiseConfigCard = ({
  rows,
  disabled,
  countryId,
  conflictLabels,
  onAdd,
  onRemove,
  onChange,
}: CruiseConfigCardProps): React.JSX.Element => {
  return (
    <section className="rounded-lg border border-zinc-200 bg-white">
      <header className="flex items-center gap-2 border-b border-zinc-100 px-5 py-3.5">
        <Ship className="h-4 w-4 text-sky-600" aria-hidden />
        <div>
          <h3 className="text-sm font-semibold tracking-tight text-zinc-900">
            Cruise configuration
          </h3>
          <p className="mt-0.5 text-xs text-zinc-500">
            Add the check-in city and dates - a cruise is auto-selected for that city on the day
            board, and can be changed there (like a hotel). Cruise stays sit on the same dated
            timeline as the hotel legs above - overlapping dates or a gap between stays block
            Generate.
          </p>
        </div>
      </header>
      <div className="space-y-5 px-5 py-5">
        <div className="space-y-2.5">
          {rows.map((row) => (
            <CruiseStayRowItem
              key={row.id}
              row={row}
              disabled={disabled}
              countryId={countryId}
              conflicts={conflictLabels.get(row.id) ?? []}
              onRemove={() => onRemove(row.id)}
              onChange={(patch) => onChange(row.id, patch)}
            />
          ))}
          {rows.length === 0 && (
            <p className="text-xs text-zinc-500">
              No cruise stays - add one if the trip includes a sailing leg.
            </p>
          )}
        </div>
        <UzButton
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={onAdd}
          disabled={disabled}
        >
          <Plus className="mr-1 h-3 w-3" aria-hidden />
          Add cruise stay
        </UzButton>
      </div>
    </section>
  );
};

const CruiseStayRowItem = ({
  row,
  disabled,
  countryId,
  conflicts,
  onRemove,
  onChange,
}: {
  row: CruiseStayRow;
  disabled: boolean;
  countryId: string | null;
  conflicts: string[];
  onRemove: () => void;
  onChange: (patch: Partial<Pick<CruiseStayRow, 'cityId' | 'checkIn' | 'checkOut'>>) => void;
}): React.JSX.Element => {
  const hasConflict = conflicts.length > 0;
  const nights = daysBetween(row.checkIn, row.checkOut);

  return (
    <div
      className={
        'rounded-lg border p-2.5 ' +
        (hasConflict ? 'border-red-300 bg-red-50/60' : 'border-zinc-200 bg-white')
      }
    >
      <div className="flex flex-wrap items-end gap-2.5">
        <div className="min-w-[140px] flex-1">
          <CityPicker
            label="Check-in city"
            value={row.cityId}
            onValueChange={(v) => onChange({ cityId: v })}
            preferCountryId={countryId}
            disabled={disabled}
          />
        </div>
        <div className="w-[150px] shrink-0">
          <UzDateTimePicker
            mode="date"
            displayFormat="dd/MM/yyyy"
            placeholder="Check-in"
            label="Check-in date"
            minDate={startOfToday()}
            value={ymdToDate(row.checkIn)}
            onValueChange={(d) => onChange({ checkIn: dateToYmd(d) })}
            disabled={disabled}
          />
        </div>
        <div className="w-[150px] shrink-0">
          <UzDateTimePicker
            mode="date"
            displayFormat="dd/MM/yyyy"
            placeholder="Check-out"
            label="Check-out date"
            minDate={
              row.checkIn ? (ymdToDate(addDays(row.checkIn, 1)) ?? undefined) : startOfToday()
            }
            value={ymdToDate(row.checkOut)}
            onValueChange={(d) => onChange({ checkOut: dateToYmd(d) })}
            disabled={disabled || !row.checkIn}
          />
        </div>
        <span className="mb-2.5 hidden shrink-0 rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600 tabular-nums sm:inline-block">
          {nights}N
        </span>
        <UzButton
          variant="ghost"
          size="icon"
          className="mb-0.5 h-9 w-9 text-zinc-400 hover:text-red-600"
          onClick={onRemove}
          disabled={disabled}
          aria-label="Remove cruise stay"
        >
          <Trash2 className="h-4 w-4" />
        </UzButton>
      </div>
      {hasConflict && (
        <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-600">
          <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
          {conflicts.join(', ')}
        </p>
      )}
    </div>
  );
};
